// Turning what C2 returns into what the Facts tab draws — all of it pure, none
// of it React, so every rule below is directly testable.
//
// Three jobs: split the two fact families, synthesise the rows for registry keys
// that have no fact at all, and count what needs attention.

import type { Fact, FactKey, Scope } from "./api";
import {
  domainTitle,
  factDefinition,
  factTitle,
  isOlderThanExpected,
  lastCheckedLabel,
  marketLabel,
  productLabel,
  statusLabel,
  valueWithUnit,
  type RowStatus,
} from "./format";

/**
 * Does a page hold every record the server has?
 *
 * `total` is the SERVER's count, not the page's. Only a page that holds all of
 * them licenses a claim about what is NOT there — an absent row in a capped
 * page means we did not fetch it, which is a different thing from it not
 * existing. `undefined` — a read that has not happened — holds nothing.
 */
export function holdsEveryRecord(
  page: { items: unknown[]; total: number } | undefined,
): boolean {
  return page !== undefined && page.items.length >= page.total;
}

/**
 * The two fact families (UX §3, HARD).
 *
 * Not every fact is about a product, and the registry already says which is
 * which — `scoped_by_formula` on `GET /v1/fact-keys`. `nutrition` and `product`
 * hang off a formula version of one product; `shipping` and `policy` hang off
 * nothing product-shaped and vary by market instead.
 *
 * They render as two SECTIONS of one tab, never two tabs and never one
 * undifferentiated list: the trap this closes is an operator switching product
 * and quietly assuming the shipping numbers below switched with it.
 */
export type Family = "per-product" | "company-wide";

export type FactRow = {
  /** Stable across renders. A missing row has no fact id, so it keys on its key. */
  id: string;
  factKey: string;
  /** The human title — the fact's name when it has one. The machine key is the second line. */
  title: string;
  /** The registry description, when it differs from the title — shown as the Definition. */
  definition: string | null;
  domain: string;
  domainTitle: string;
  market: string;
  product: string;
  valueLabel: string;
  status: RowStatus;
  statusLabel: string;
  lastChecked: string;
  approvedAt: string | null;
  /** `null` for a synthesised missing row — there is no record to open. */
  fact: Fact | null;
  /**
   * What a FIRST value for this row would be drafted against.
   *
   * A missing row has no fact to copy a formula version off, so the scope it was
   * synthesised for carries one. Without it, publishing a first value for a
   * per-product key would create a fact attached to no product — the exact
   * unreadable record the product rule exists to prevent.
   */
  formulaVersionId: string | null;
  productKey: string | null;
  /** The registry entry, so the pane can read `value_type` and `unit` for a first value. */
  key: FactKey | undefined;
};

export type DomainGroup = { domain: string; title: string; rows: FactRow[] };

/** Registry keys belonging to a family. */
export function keysForFamily(keys: FactKey[], family: Family): FactKey[] {
  return keys.filter((key) =>
    family === "per-product" ? key.scoped_by_formula : !key.scoped_by_formula,
  );
}

/** Facts belonging to a family. A company-wide fact carries no formula version. */
export function factsForFamily(facts: Fact[], family: Family): Fact[] {
  return facts.filter((fact) =>
    family === "per-product" ? fact.formula_version_id !== null : fact.formula_version_id === null,
  );
}

/**
 * "Superseded by v4", from the siblings we actually hold.
 *
 * The store does not return a pointer to the record that replaced this one, so
 * the version is read off the highest sibling that shares this record's whole
 * identity — key, scope AND formula version. All three are load-bearing:
 *
 *   - `fact_key` — obviously.
 *   - `scope_id` — a company-wide key has one row per market, so a UK v4 would
 *     otherwise name itself as the successor of a GCC v3.
 *   - `formula_version_id` — every per-product nutrition fact carries the
 *     WILDCARD scope, so a whole family shares one `scope_id`. In "all products"
 *     mode the siblings handed in are the entire family, and matching on key and
 *     scope alone would read MCT Oil's v4 as the successor of Keto Collagen's
 *     v3 — pointing the reader at a version that does not exist for the product
 *     they are looking at.
 *
 * Only an APPROVED sibling counts. A superseded record was replaced by the
 * version that went LIVE in its place — a draft has published nothing and
 * superseded nothing, so counting it named v5 as the successor of v3 while v4
 * was the number customers were actually seeing. The badge is a claim about the
 * storefront, and it has to name the record that owns it.
 *
 * When there is no such sibling in view the badge stays plain "Superseded" — an
 * inferred `version + 1` would name a record that may not exist.
 */
function supersedingVersion(fact: Fact, siblings: Fact[]): number | undefined {
  const newer = siblings
    .filter(
      (other) =>
        other.status === "approved" &&
        other.fact_key === fact.fact_key &&
        other.scope_id === fact.scope_id &&
        other.formula_version_id === fact.formula_version_id &&
        other.version > fact.version,
    )
    .map((other) => other.version);
  return newer.length > 0 ? Math.max(...newer) : undefined;
}

function rowFromFact(fact: Fact, keys: FactKey[], siblings: Fact[], now: Date): FactRow {
  const key = keys.find((k) => k.fact_key === fact.fact_key);
  const title = factTitle(fact.fact_key, key, fact);
  const status = fact.status as RowStatus;
  const version =
    status === "superseded" ? supersedingVersion(fact, siblings) : undefined;
  return {
    id: fact.fact_id,
    factKey: fact.fact_key,
    title,
    definition: factDefinition(title, key),
    domain: key?.domain ?? domainOf(fact.fact_key),
    domainTitle: domainTitle(key?.domain ?? domainOf(fact.fact_key)),
    market: marketLabel(fact.scope),
    product: productLabel(fact),
    valueLabel: valueWithUnit(fact.value, fact.unit ?? key?.unit ?? null),
    status,
    // One label builder, not two: the sentence lives in `statusLabel`, and this
    // only supplies the version it could not know.
    statusLabel: statusLabel(status, version),
    lastChecked: lastCheckedLabel(fact.approved_at, now),
    approvedAt: fact.approved_at,
    fact,
    formulaVersionId: fact.formula_version_id,
    productKey: fact.product_key,
    key,
  };
}

/**
 * A registry key with no fact row at all, rendered anyway (UX Flow C edge).
 *
 * An unregistered number cannot hide by being absent: if the key exists and no
 * value was ever published, the reader sees the hole rather than a list that
 * quietly omits it.
 */
function missingRow(
  key: FactKey,
  scope: Scope,
  product: string,
  scopeKey: string,
  formulaVersionId: string | null,
  productKey: string | null,
): FactRow {
  const title = factTitle(key.fact_key, key);
  return {
    id: `missing:${scopeKey}:${key.fact_key}`,
    factKey: key.fact_key,
    title,
    definition: factDefinition(title, key),
    domain: key.domain,
    domainTitle: domainTitle(key.domain),
    market: marketLabel(scope),
    product,
    valueLabel: valueWithUnit(null, key.unit),
    status: "missing",
    statusLabel: statusLabel("missing"),
    lastChecked: lastCheckedLabel(null),
    approvedAt: null,
    fact: null,
    formulaVersionId,
    productKey,
    key,
  };
}

const WILDCARD: Scope = { region: "*", store: "*", channel: "*", locale: "*" };

function domainOf(factKey: string): string {
  return factKey.includes(".") ? factKey.slice(0, factKey.indexOf(".")) : factKey;
}

/**
 * Does a record mean this key is ANSWERED for its product — published, or on
 * its way there?
 *
 * Only `approved` and `draft` do. The set this feeds decides which registry
 * keys get a synthesised Missing row, and it used to hold every record whatever
 * its status. So a key whose only record was REJECTED read as present: no
 * Missing row was made for it, and no triage chip counts a rejected row either
 * — `awaiting` counts drafts, `missing` counts the synthesised rows, `older`
 * counts approved ones (`isStaleRow`). A storefront hole with a turned-down
 * correction sitting in front of it was therefore invisible on every count the
 * operator has, on a surface whose whole job is that a blank panel line cannot
 * hide.
 *
 * The same reasoning reaches a key holding only `superseded` records: an old
 * version is a historical record, not a published value.
 *
 * A `draft` counts as answered because it is already the operator's next step
 * under the `awaiting` chip. Synthesising a Missing row beside it would count
 * one piece of work twice and offer a "publish a first value" route that
 * ignores the correction someone has already written.
 *
 * The Missing row this admits sits ALONGSIDE the rejected record, not instead
 * of it: the catalogue lists records, and a key already renders one row per
 * version it holds. The rejected row says what was turned down; the Missing row
 * says the panel line is still blank, and is the row that opens the first-value
 * flow.
 */
export function answersItsKey(status: string): boolean {
  return status === "approved" || status === "draft";
}

/**
 * WHICH hole a record fills — the identity a Missing row is synthesised against.
 *
 * All three parts are load-bearing, and this is the same identity every other
 * resolution on this surface already uses (`supersedingVersion`,
 * `publishedAmong`, `liveValueFor`):
 *
 *   - `fact_key` — obviously.
 *   - `product_key` — a key published for MCT Oil says nothing about whether
 *     Keto Collagen has it, so in all-products mode one product's value would
 *     otherwise hide the hole in every other.
 *   - `formula_version_id` — a panel line is filled for a FORMULA VERSION, not
 *     for a product in the abstract. A product moved v6 → v7 carries its v6
 *     approvals forward as historical records, and matching on product and key
 *     alone read one of them as proof the v7 panel line is filled: no Missing
 *     row was synthesised, the missing chip under-counted, and an Approved row
 *     stood in front of a blank line on the storefront. That is the
 *     blank-panel-that-hides class this whole tab exists to close.
 *
 * A company-wide fact carries `null` for both product and formula version and a
 * company-wide scope carries `null` for both too, so this collapses to the fact
 * key alone for that family — which is the whole of its identity.
 *
 * The formula version a scope is checked against is the product's CURRENT one
 * (`productsIn` reads it off the newest fact, `GET /v1/facts` ordering newest
 * first). So the claim made here is "this key has no value under the version
 * now in force", which is the claim the panel needs.
 */
function presenceKey(
  productKey: string | null,
  formulaVersionId: string | null,
  factKey: string,
): string {
  return `${productKey ?? ""}|${formulaVersionId ?? ""}|${factKey}`;
}

/**
 * The rows of one family, grouped under their domain headings.
 *
 * `complete` says whether the caller holds every fact of this family — the
 * server's `total` for the query, compared against what came back. Missing rows
 * are ONLY synthesised when it is true: on a truncated page a key can look
 * absent purely because its fact is on page 2, and inventing a "Missing" badge
 * for a value that is published is the one error this surface must never make.
 */
export function buildGroups({
  facts,
  keys,
  family,
  complete,
  scopes,
  now = new Date(),
}: {
  facts: Fact[];
  keys: FactKey[];
  family: Family;
  complete: boolean;
  /**
   * The product scopes a missing row is synthesised FOR — one entry when the
   * section is scoped to a single product, the whole catalogue in "all products"
   * mode, and a single `{ key: null }` entry for the company-wide family.
   *
   * Without this, "Fibre per 100 g is missing" cannot say WHICH product it is
   * missing for, and in all-products mode a key published for one product would
   * hide the hole in every other.
   */
  scopes: Array<{ key: string | null; label: string; formulaVersionId: string | null }>;
  now?: Date;
}): DomainGroup[] {
  const familyFacts = factsForFamily(facts, family);
  const familyKeys = keysForFamily(keys, family);

  const rows = familyFacts.map((fact) => rowFromFact(fact, keys, familyFacts, now));

  if (complete) {
    const present = new Set(
      familyFacts
        .filter((fact) => answersItsKey(fact.status))
        .map((fact) => presenceKey(fact.product_key, fact.formula_version_id, fact.fact_key)),
    );
    for (const scope of scopes) {
      for (const key of familyKeys) {
        if (!present.has(presenceKey(scope.key, scope.formulaVersionId, key.fact_key))) {
          rows.push(
            missingRow(
              key,
              WILDCARD,
              scope.label,
              scope.key ?? "every-product",
              scope.formulaVersionId,
              scope.key,
            ),
          );
        }
      }
    }
  }

  const byDomain = new Map<string, FactRow[]>();
  for (const row of rows) {
    const bucket = byDomain.get(row.domain);
    if (bucket) bucket.push(row);
    else byDomain.set(row.domain, [row]);
  }

  // Within a domain, rows follow the REGISTRY's own order, not the alphabet.
  // A nutrition panel reads energy, protein, fibre, total fat — the order the
  // registry lists them and the order a person checking a label expects. Sorted
  // by title instead, fibre jumped above protein and the screen no longer
  // matched the panel it is the source for. Title is the tiebreak for a key the
  // registry does not hold (a fact whose key was retired), so an unregistered
  // row still lands somewhere stable rather than at a random index.
  const registryOrder = new Map(keys.map((key, index) => [key.fact_key, index]));
  const positionOf = (row: FactRow) => registryOrder.get(row.factKey) ?? Number.MAX_SAFE_INTEGER;

  // Domains follow the registry too, for the same reason and by the same rule:
  // a domain sits where its FIRST key sits. Alphabetically, Policy came before
  // Shipping and the company-wide section opened on the returns window rather
  // than on the shipping thresholds the registry leads with.
  const domainOrder = new Map<string, number>();
  keys.forEach((key, index) => {
    if (!domainOrder.has(key.domain)) domainOrder.set(key.domain, index);
  });
  const domainPosition = (domain: string) =>
    domainOrder.get(domain) ?? Number.MAX_SAFE_INTEGER;

  return [...byDomain.entries()]
    .sort(([a], [b]) => domainPosition(a) - domainPosition(b) || a.localeCompare(b))
    .map(([domain, groupRows]) => ({
      domain,
      title: domainTitle(domain),
      rows: groupRows.sort(
        (a, b) =>
          positionOf(a) - positionOf(b) ||
          a.title.localeCompare(b.title) ||
          // Product before market. Without it, one key's rows for two products
          // came out in whatever order the facts arrived, so "compare across
          // products" alternated MCT Oil C8 and MCT Coffee Creamer from one key
          // to the next and the eye could not run down a product.
          a.product.localeCompare(b.product) ||
          a.market.localeCompare(b.market),
      ),
    }));
}

export type NeedsAttention = {
  awaiting: number;
  missing: number;
  older: number;
};

/**
 * The three counts on the strip, over the rows the operator is actually looking
 * at — this product plus every company-wide fact, both families together.
 *
 * A missing returns-window is as publishable a hole as a missing fibre value, so
 * neither family is excluded. The scope is STATED next to the counts rather than
 * implied: "N of the page" and "N of everything" are different claims and the
 * reader has no way to tell them apart from a bare number.
 */
export function needsAttention(groups: DomainGroup[], now: Date = new Date()): NeedsAttention {
  const rows = groups.flatMap((group) => group.rows);
  return {
    awaiting: rows.filter((row) => row.status === "draft").length,
    missing: rows.filter((row) => row.status === "missing").length,
    older: rows.filter((row) => isStaleRow(row, now)).length,
  };
}

/**
 * "Older than expected" — over the rows a person can actually act on.
 *
 * ONLY an approved row counts. A superseded or rejected row is a historical
 * record carrying an old `approved_at`, so counting it inflated the chip with
 * rows that need nothing done to them: a key corrected three times reported
 * three stale values while one number was live.
 *
 * This is the single predicate behind BOTH the chip's count and the rows the
 * chip filters to. Two definitions of "stale" would mean a chip that says 7 and
 * a list that shows 2.
 */
export function isStaleRow(row: FactRow, now: Date = new Date()): boolean {
  return row.status === "approved" && isOlderThanExpected(row.approvedAt, now);
}

export type RowFilters = {
  domain?: string;
  market?: string;
  status?: string;
};

/**
 * Domain and market narrowing, applied to rows already in hand.
 *
 * C2 filters by fact key, status and product — not by domain or market — so
 * these two cannot be pushed to the server. They are therefore only offered when
 * the caller holds the family's COMPLETE set (see `buildGroups`), which is what
 * keeps "3 of 12" from meaning "3 of the 12 that happened to be on this page".
 */
export function filterGroups(groups: DomainGroup[], filters: RowFilters): DomainGroup[] {
  return groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter(
        (row) =>
          (!filters.domain || row.domain === filters.domain) &&
          (!filters.market || row.market === filters.market) &&
          (!filters.status || row.status === filters.status),
      ),
    }))
    .filter((group) => group.rows.length > 0);
}

/**
 * The search box, applied to rows already in hand (KET-6326).
 *
 * Case-insensitive substring over what the reader can SEE or would recognise:
 * the machine key, the displayed title, the registry's description or
 * definition, and the displayed value — each market's row carries its own, so
 * "€50" finds the EU threshold and not the UK one. Runs AFTER `filterGroups`,
 * so it narrows the Domain / Market / Status view rather than replacing it, and
 * a domain left with no rows drops its heading. An empty query is no search.
 */
export function searchGroups(groups: DomainGroup[], query: string): DomainGroup[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === "") return groups;
  return groups
    .map((group) => ({ ...group, rows: group.rows.filter((row) => rowMatches(row, needle)) }))
    .filter((group) => group.rows.length > 0);
}

function rowMatches(row: FactRow, needle: string): boolean {
  return [
    row.factKey,
    // `title` already holds the fact's own name when it has one; the
    // registry's name is searched too in case the two differ.
    row.title,
    row.key?.name,
    row.key?.description,
    row.key?.definition,
    row.valueLabel,
  ].some(
    (text) => typeof text === "string" && text.toLocaleLowerCase().includes(needle),
  );
}

/** The distinct products the loaded facts name, for the product picker. */
/**
 * The distinct products the loaded facts name, for the product picker.
 *
 * The formula version is the one on the product's most recently created fact —
 * `GET /v1/facts` orders newest first — because that is the version a first
 * value for a missing key should be drafted against. A product whose facts
 * straddle two formula versions therefore picks the newer, which is the same
 * choice a person would make and is stated here rather than left implicit.
 */
export function productsIn(facts: Fact[]): Array<{
  product_key: string;
  product_name: string;
  formula_version_id: string | null;
  formula_version_label: string | null;
  formula_effective_from: string | null;
}> {
  const seen = new Map<string, ReturnType<typeof productsIn>[number]>();
  for (const fact of facts) {
    if (!fact.product_key || !fact.product_name) continue;
    if (!seen.has(fact.product_key)) {
      seen.set(fact.product_key, {
        product_key: fact.product_key,
        product_name: fact.product_name,
        formula_version_id: fact.formula_version_id,
        formula_version_label: fact.formula_version_label,
        formula_effective_from: fact.formula_effective_from,
      });
    }
  }
  return [...seen.values()].sort((a, b) => a.product_name.localeCompare(b.product_name));
}

/**
 * What the operator has open — held as the row's IDENTITY, not just its id.
 *
 * A synthesised Missing row is the one selection whose id is not a record's:
 * it is `missing:<scope>:<key>`, invented here because no fact exists. The
 * moment a first value is drafted for that key the row stops being
 * synthesised — `answersItsKey` counts a draft as present — so the id the pane
 * was opened on no longer matches anything, and looking the selection up by id
 * alone returned nothing. The pane then replaced the operator's half-finished
 * first value with "This fact is no longer in the list", taking the typed
 * number, the source and Approve off the screen in the middle of Flow C.
 *
 * Carrying the key and the product is what lets the selection FOLLOW the row
 * across that change. Carrying `synthesised` is what keeps the following
 * narrow: only a row that never had a record of its own is allowed to resolve
 * to a different record, so a real fact that genuinely leaves the list still
 * says so rather than silently redirecting the pane at its neighbour.
 */
export type Selection = {
  id: string;
  factKey: string;
  /** `null` for a company-wide row — it belongs to no product. */
  productKey: string | null;
  /** A Missing row invented for a registry key, holding no record. */
  synthesised: boolean;
};

export function selectionFor(row: FactRow): Selection {
  return {
    id: row.id,
    factKey: row.factKey,
    productKey: row.productKey,
    synthesised: row.fact === null,
  };
}

/**
 * The row the open pane is about.
 *
 * By id first — that is the selection, and for every row backed by a record it
 * is the whole answer. A SYNTHESISED row gets one further step: when its id
 * matches nothing, the row that now answers the same key for the same product
 * is the row it became.
 *
 * Only `draft` and `approved` are followed to, because only those two can have
 * displaced it — `answersItsKey` is what decides whether a Missing row is
 * synthesised, and it admits exactly those. A superseded or rejected record
 * sits ALONGSIDE a Missing row rather than replacing it, so resolving to one
 * would move the pane onto a record that is not the reason the row went away.
 *
 * `undefined` when nothing matches, which the pane still renders as "no longer
 * in the list" — the fallback narrows that message to the cases it is true of,
 * it does not remove it.
 */
export function selectedRow(
  groups: DomainGroup[],
  selection: Selection | null,
): FactRow | undefined {
  if (selection === null) return undefined;
  const rows = groups.flatMap((group) => group.rows);
  const exact = rows.find((row) => row.id === selection.id);
  if (exact) return exact;
  if (!selection.synthesised) return undefined;
  const sameKey = rows.filter(
    (row) => row.factKey === selection.factKey && row.productKey === selection.productKey,
  );
  return (
    sameKey.find((row) => row.status === "draft") ??
    sameKey.find((row) => row.status === "approved")
  );
}
